import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { ReactNode } from 'react';

import './styles/fonts.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';

import { I18nProvider } from './i18n/I18nProvider';
import { ThemeProvider } from './theme/ThemeProvider';
import { Router } from './router/Router';
import { ToastProvider } from './ui/Toast';
import { App } from './app/App';
import { DidNotStartScreen } from './app/DidNotStartScreen';
import { resolveEntry } from './entry';

/**
 * Only what both entry points share is mounted here. The API client and the
 * session belong to one console or the other, so they are mounted inside the
 * console that owns them — see src/app/AccountApp.tsx and ClusterApp.tsx.
 *
 * Which of the two this is comes first, from the control plane, and nothing is
 * mounted until it answers: the router parses addresses per entry point, and
 * there is no third set to parse them with while an answer is on its way.
 */
const container = document.getElementById('root');
if (container === null) throw new Error('#root is missing from index.html');

const root = createRoot(container);

/** What both outcomes are wrapped in: neither depends on which console this is. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <StrictMode>
      <I18nProvider>
        <ThemeProvider>{children}</ThemeProvider>
      </I18nProvider>
    </StrictMode>
  );
}

resolveEntry().then(
  () => {
    root.render(
      <Shell>
        <ToastProvider>
          <Router>
            <App />
          </Router>
        </ToastProvider>
      </Shell>,
    );
  },
  (error: unknown) => {
    root.render(
      <Shell>
        <DidNotStartScreen detail={error instanceof Error ? error.message : String(error)} />
      </Shell>,
    );
  },
);
